import { theme } from '../styles/theme';

interface ColorSwatchProps {
  name: string;
  color: string;
  textColor?: string;
}

function ColorSwatch({ name, color, textColor = 'text-gray-900' }: ColorSwatchProps) {
  return (
    <div className="flex flex-col">
      <div
        className="h-20 rounded-lg border border-gray-200 shadow-sm flex items-center justify-center"
        style={{ backgroundColor: color }}
      >
        <span className={`text-xs font-mono ${textColor}`}>{color}</span>
      </div>
      <p className="text-xs text-gray-600 mt-2 font-medium">{name}</p>
    </div>
  );
}

interface ColorScaleProps {
  title: string;
  colors: Record<string, string>;
}

function ColorScale({ title, colors }: ColorScaleProps) {
  return (
    <div className="mb-8">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      <div className="grid grid-cols-5 gap-4">
        {Object.entries(colors).map(([shade, color]) => (
          <ColorSwatch
            key={shade}
            name={shade}
            color={color}
            textColor={['50', '100', '200', '300'].includes(shade) ? 'text-gray-900' : 'text-white'}
          />
        ))}
      </div>
    </div>
  );
}

export default function ColorSystemGuide() {
  return (
    <div className="max-w-7xl mx-auto p-8 space-y-12">
      {/* Header */}
      <div className="border-b border-gray-200 pb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Color System Guide</h1>
        <p className="text-gray-600">
          Complete color palette for the Agentic Care Management System
        </p>
      </div>

      {/* Primary Colors */}
      <section>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Brand Colors</h2>
        <ColorScale title="Primary Purple" colors={theme.colors.primary} />
        <ColorScale title="Blue" colors={theme.colors.blue} />
      </section>

      {/* Semantic Colors */}
      <section>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Semantic Colors</h2>
        <ColorScale title="Green (Success)" colors={theme.colors.green} />
        <ColorScale title="Red (Error/Critical)" colors={theme.colors.red} />
        <ColorScale title="Amber (Warning)" colors={theme.colors.amber} />
      </section>

      {/* Neutral Colors */}
      <section>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Neutral Colors</h2>
        <ColorScale title="Gray" colors={theme.colors.gray} />
      </section>

      {/* Status Colors */}
      <section>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Status Colors</h2>
        <div className="grid grid-cols-4 gap-6">
          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-gray-900">Success</h4>
            <ColorSwatch name="Background" color={theme.semantic.status.successBg} />
            <ColorSwatch name="Color" color={theme.semantic.status.success} textColor="text-white" />
            <ColorSwatch name="Border" color={theme.semantic.status.successBorder} />
          </div>
          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-gray-900">Warning</h4>
            <ColorSwatch name="Background" color={theme.semantic.status.warningBg} />
            <ColorSwatch name="Color" color={theme.semantic.status.warning} textColor="text-white" />
            <ColorSwatch name="Border" color={theme.semantic.status.warningBorder} />
          </div>
          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-gray-900">Error</h4>
            <ColorSwatch name="Background" color={theme.semantic.status.errorBg} />
            <ColorSwatch name="Color" color={theme.semantic.status.error} textColor="text-white" />
            <ColorSwatch name="Border" color={theme.semantic.status.errorBorder} />
          </div>
          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-gray-900">Info</h4>
            <ColorSwatch name="Background" color={theme.semantic.status.infoBg} />
            <ColorSwatch name="Color" color={theme.semantic.status.info} textColor="text-white" />
            <ColorSwatch name="Border" color={theme.semantic.status.infoBorder} />
          </div>
        </div>
      </section>

      {/* Priority Colors */}
      <section>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Priority Colors</h2>
        <div className="grid grid-cols-4 gap-6">
          {Object.entries(theme.semantic.priority).map(([priority, colors]) => (
            <div key={priority} className="space-y-2">
              <h4 className="text-sm font-semibold text-gray-900 capitalize">{priority}</h4>
              <ColorSwatch name="Background" color={colors.bg} />
              <ColorSwatch name="Text" color={colors.text} textColor="text-white" />
              <ColorSwatch name="Border" color={colors.border} />
            </div>
          ))}
        </div>
      </section>

      {/* Gradients */}
      <section>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Gradients</h2>
        <div className="grid grid-cols-3 gap-6">
          {Object.entries(theme.gradients).map(([name, gradient]) => (
            <div key={name} className="space-y-2">
              <div
                className="h-32 rounded-lg border border-gray-200 shadow-sm flex items-center justify-center"
                style={{ background: gradient }}
              >
                <span className="text-white text-sm font-semibold capitalize">{name}</span>
              </div>
              <p className="text-xs text-gray-600 font-mono break-all">{gradient}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Text Colors */}
      <section>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Text Colors</h2>
        <div className="bg-white p-8 rounded-lg border border-gray-200">
          <div className="space-y-4">
            <p className="text-4xl" style={{ color: theme.semantic.text.primary }}>
              Primary Text - The quick brown fox
            </p>
            <p className="text-2xl" style={{ color: theme.semantic.text.secondary }}>
              Secondary Text - The quick brown fox
            </p>
            <p className="text-xl" style={{ color: theme.semantic.text.tertiary }}>
              Tertiary Text - The quick brown fox
            </p>
            <p className="text-lg" style={{ color: theme.semantic.text.disabled }}>
              Disabled Text - The quick brown fox
            </p>
          </div>
        </div>
      </section>

      {/* Component Examples */}
      <section>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Component Examples</h2>

        {/* Buttons */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Buttons</h3>
          <div className="flex gap-4 flex-wrap">
            <button className="px-6 py-3 bg-purple-600 hover:bg-purple-700 active:bg-purple-800 text-white rounded-lg font-medium transition-colors">
              Primary Button
            </button>
            <button className="px-6 py-3 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white rounded-lg font-medium transition-colors">
              Secondary Button
            </button>
            <button className="px-6 py-3 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded-lg font-medium transition-colors">
              Outline Button
            </button>
            <button className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors">
              Success Button
            </button>
            <button className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors">
              Danger Button
            </button>
          </div>
        </div>

        {/* Badges */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Badges</h3>
          <div className="flex gap-3 flex-wrap">
            <span className="px-3 py-1 bg-blue-100 text-blue-700 border border-blue-200 rounded-full text-sm font-medium">
              Primary
            </span>
            <span className="px-3 py-1 bg-green-100 text-green-700 border border-green-200 rounded-full text-sm font-medium">
              Success
            </span>
            <span className="px-3 py-1 bg-amber-100 text-amber-700 border border-amber-200 rounded-full text-sm font-medium">
              Warning
            </span>
            <span className="px-3 py-1 bg-red-100 text-red-700 border border-red-200 rounded-full text-sm font-medium">
              Danger
            </span>
            <span className="px-3 py-1 bg-gray-100 text-gray-700 border border-gray-200 rounded-full text-sm font-medium">
              Neutral
            </span>
          </div>
        </div>

        {/* Priority Badges */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Priority Badges</h3>
          <div className="flex gap-3 flex-wrap">
            <span className="px-3 py-1 bg-red-100 text-red-800 border border-red-200 rounded-full text-xs font-semibold uppercase">
              Critical
            </span>
            <span className="px-3 py-1 bg-amber-100 text-amber-800 border border-amber-200 rounded-full text-xs font-semibold uppercase">
              High
            </span>
            <span className="px-3 py-1 bg-blue-100 text-blue-800 border border-blue-200 rounded-full text-xs font-semibold uppercase">
              Medium
            </span>
            <span className="px-3 py-1 bg-gray-100 text-gray-600 border border-gray-200 rounded-full text-xs font-semibold uppercase">
              Low
            </span>
          </div>
        </div>
      </section>

      {/* Accessibility Info */}
      <section className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Accessibility Notes</h2>
        <div className="space-y-2 text-sm text-gray-700">
          <p>✅ All color combinations meet WCAG 2.1 Level AA contrast requirements</p>
          <p>✅ Minimum 4.5:1 contrast ratio for normal text</p>
          <p>✅ Minimum 3:1 contrast ratio for large text (18pt+)</p>
          <p>✅ Colors are never used as the sole means of conveying information</p>
          <p>✅ Icons and text labels accompany all color-coded elements</p>
        </div>
      </section>
    </div>
  );
}
